import { Box, Container, Paper, Stack, Tab, Tabs, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import GroupService from '../../../../shared/services/group/group.service';
import { IGroupDetail } from '../../../../shared/services/group/group.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import TransactionService from '../../../../shared/services/transaction/transaction.service';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service';
import { EmissionsAssumptionsTable } from '../../../presentation-components/emissons-components/overview/emissions-assumptions-table';
import { a11yProps, deleteQueryParamFromUrl } from '../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { InformationBox } from '../../../presentation-components/miscellaneous-components/information-box';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { EmissionsActionsTab } from '../../../presentation-components/emissons-components/overview/emissions-actions/emissions-actions-tab';
import { ErrorOutline } from '@mui/icons-material';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { EmissionsGroupButtons } from '../../../presentation-components/emissons-components/overview/emissions-display/emissions-group-buttons';
import { EmissionsOverviewGroupHeading } from '../../../presentation-components/emissons-components/overview/emissions-display/emissions-overview-group-heading';
import { EntityFindingsPanel } from '../../../presentation-components/readiness-components/entity-findings-panel';
import { Emissions } from '../../../presentation-components/emissons-components/overview/emissions';
import { EmissionsGoodCatalogue } from '../../../presentation-components/emissons-components/overview/emissions-good-catalogue';
import { EmissionsAddManual } from '../../../presentation-components/emissons-components/overview/emissions-add-manual';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked';
import { getPreferredSubsidiary } from '../../../../shared/utils/subsidiary-utils';
import { isGoodsGroupId, isSoldProductGroupId } from '../../../../shared/utils/group-ids-utils';
import ReportActionService from '../../../../shared/services/report-action/report-action.service';
import { IReportAction } from '../../../../shared/services/report-action/report-action.model';
import { NotesPanel } from '../../../presentation-components/notes-components/notes-panel';
import { userHasRequiredRole } from '../../../../shared/utils/role-utils';
import { getGroupPageTabIndexes } from '../../../../shared/utils/emissions-group-tabs';

export const EmissionsGroupPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const initialReportingFilter = searchParams.get('emissionsTab') === 'excluded' ? 'excluded' : 'included'
  //Services
  const groupService = GroupService.getInstance();
  const reportPeriodService = new ReportPeriodService();
  const transactionService = new TransactionService(); 
  const subsidiaryService = SubsidiaryService.getInstance();
  const conversionSourceService = new ConversionSourceService();
  const reportActionService = new ReportActionService();
  //Hooks
  const isReportPeriodLocked = useIsReportPeriodLocked()
  //Context
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const userNotificationsContext = useContext(UserNotificationsContext);
  const reportPeriodContext = useContext(ReportPeriodContext);
  //State
  const [selectedSubsidiaryId, setSelectedSubsidiaryId] = useState<string | undefined>()
  const [conversionSources, setConversionSources] = useState<IConversionSource[]>()
  const [selectedEmissionFactorSourceId, setSelectedEmissionFactorSourceId] = useState<string | undefined>()
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([])
  const [group, setGroup] = useState<IGroupDetail>()
  const [status, setStatus] = React.useState<'outstanding' | 'completed' | 'upcoming'>('outstanding');
  const [value, setValue] = React.useState(0)
  const [viewActions, setViewActions] = useState<'actions' | 'input'>('actions')
  const [initialAction, setInitialAction] = useState<IReportAction>()
  const [viewEmissions, setViewEmissions] = useState<'groupTable' | 'categories'>('groupTable')
  const [activeCategoryId, setActiveCategoryId] = useState<string>('')
  const [backgroundColor, setBackgroundColor] = useState<string>('common.white')
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [actionsTotalRecords, setActionsTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(12)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Variables
  // Every group shows the Tasks tab on an open period: system-generated tasks exist for the
  // utilities/vehicle groups, and tasks created from readiness findings can live on any group.
  const showTasksTab = !isReportPeriodLocked
  // Emissions is always the first tab, so the page opens on it. Tasks sits second when shown, and
  // the Assumptions tab is admin and analyst only (the /assumption API refuses anyone else), so a
  // data owner does not see it. The tabs after them shift; worked out in emissions-group-tabs.ts.
  const showAssumptionsTab = userHasRequiredRole(userDetails, ['admin', 'analyst'])
  const tabIndexes = getGroupPageTabIndexes({ showTasksTab, showAssumptionsTab })
  const emissionsTabIndex = tabIndexes.emissions
  const tasksTabIndex = tabIndexes.tasks
  const assumptionsTabIndex = tabIndexes.assumptions
  const notesTabIndex = tabIndexes.notes
  // Period notes are written by admins and analysts; a data owner can read them.
  const canWriteNotes = !!userDetails?.roles?.some((role) => role === 'admin' || role === 'analyst')
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  //Use Effects
  useEffect(() => {
    if (id) {
      getGroup()
    }
  }, [id, value, reportPeriodContext.lastSelectedReportId, inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [status, order, orderBy, reportPeriodContext.lastSelectedReportId])

  useEffect(() => {
    if (userDetails && subsidiaries) {
      const preferred = getPreferredSubsidiary(subsidiaries, userDetails)
      if (preferred) {
        setSelectedSubsidiaryId(preferred.id)
        setSelectedEmissionFactorSourceId(preferred.defaultConversionSourceId)
      }
    }
  }, [userDetails, subsidiaries])

  // Quick add from the emissions overview: land on the Emissions tab with the Add
  // Emission category grid already open. Keyed on searchParams (not run-once) so the
  // deep link also works when the user is already on this page and only the query
  // string changes.
  useEffect(() => {
    if (searchParams.has('addEmission')) {
      setValue(emissionsTabIndex)
      setViewEmissions('categories')
      setActiveCategoryId('')
      deleteQueryParamFromUrl(searchParams, 'addEmission')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, id])

  useEffect(() => {
    if (searchParams.has('tabId')) {
      const token = searchParams.get('tabId')
      if (token) {
        setValue(parseInt(token))
      }
      deleteQueryParamFromUrl(searchParams, 'tabId')
    }
    deleteQueryParamFromUrl(searchParams, 'emissionsTab')
    // Deep link from a task list (My Tasks, dashboard widget): open the named action's
    // entry form directly instead of leaving the user to find it in the Tasks tab.
    const actionId = searchParams.get('actionId')
    if (actionId && !isReportPeriodLocked) {
      reportActionService.getOneAction(actionId)
        .then(action => {
          setInitialAction(action)
          setValue(tasksTabIndex)
          setViewActions('input')
        })
        .catch(err => {
          console.log(err)
        })
      deleteQueryParamFromUrl(searchParams, 'actionId')
    }
    conversionSourceService.getAll(true, 'conversion')
      .then(response => {
        setConversionSources(response)
        console.log(response)
      })
      .catch(err => {
        console.log(err)
      })
    subsidiaryService.getAll()
      .then(response => {
        setSubsidiaries(response)
      })
      .catch(err => {
        console.log(err)
      })
  }, [])


  const getGroup = () => {
    if (id && reportPeriodContext.lastSelectedReportId) {
      groupService.getGroupWithActionsForReportPeriodWithPagination(id, reportPeriodContext.lastSelectedReportId, inputPage, perPage, orderBy, order, status)
        .then(response => {
          setGroup(response)
          setActionsTotalRecords(response.reportActions.actions.count);
        })
        .catch(err => {
          console.log(err)
        })
    }
  }

  const changeHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setActiveCategoryId('');
  };

  const showGroupHandler = () => {
    setViewEmissions('groupTable')
    setActiveCategoryId('')
  }

  const showCategoriesHandler = () => {
    setValue(emissionsTabIndex)
    setViewEmissions('categories')
    getGroup()
  }

  const showCategoryFormHandler = (categoryId: string) => {
    // An empty id closes the form and returns to the category card grid (Cancel).
    if (!categoryId) {
      setActiveCategoryId('')
      return
    }
    conversionSourceService.getConversionSourceForCategory(categoryId, 'conversion', group?.scope!, true)
      .then(response => {
        setConversionSources(response)
        setActiveCategoryId(prev => categoryId)
        setBackgroundColor('secondary.light');
        const selectedSubsidiary = subsidiaries.find(subsidiary => subsidiary.id === selectedSubsidiaryId);
        const defaultConversionSourceId = selectedSubsidiary?.defaultConversionSourceId;
        const foundConversionSource = response.find(conversionSource => conversionSource.id === defaultConversionSourceId);
        if (foundConversionSource) {
          setSelectedEmissionFactorSourceId(foundConversionSource.id);
        } else if (response.length === 1) {
          // The default source cannot price this category and exactly one source can (on-site
          // generation, for example, exists only in the zero-emission source). Select it, so the
          // factor search works straight away instead of sending the user to Advanced to find the
          // only option there is. With several candidates the choice stays with the user.
          setSelectedEmissionFactorSourceId(response[0].id);
        } else {
          setSelectedEmissionFactorSourceId('');
        }
      })
      .catch(err => {
        console.log(err)
      })
  }

  const showActionHandler = () => {
    setViewActions('actions')
  }

  const emissionFactorSourceHandler = (newSelectedEmissionFactorSource: IConversionSource) => {
    setSelectedEmissionFactorSourceId(newSelectedEmissionFactorSource.id)
  }

  const handleTransactionSubmit = (form: any) => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Not Allowed',
        message: `The selected report period is locked and cannot be modified`,
        buttonCopy: 'Ok',
        hideCancelButton: true
      })
    } else {
      if (!isFormSubmitting) {
        setIsFormSubmitting(true)
        transactionService.postTransaction(form)
          .then((response: any) => {
            setIsFormSubmitting(false);
            if (response?.id) {
              navigate(`/emissions/overview/${response.id}?backLink=/emissions/overview/group/${id}&backText=Back+to+group`);
            } else {
              onSave();
            }
          })
          .catch(err => {
            setIsFormSubmitting(false)
            console.log(err)
            if (err.response.data.code === 1) {
              userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' })
            } else {
              const message = err?.response?.data?.message || 'There was an error, please try again'
              userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
            }
          })
      } else {
        userNotificationsContext.openSnackbar({ open: true, message: 'Saving in progress, please wait' });
      }
    }
  }

  const onSave = () => {
    userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' })
    setViewEmissions('groupTable')
  }

  const completeAllActionsHandler = () => {
    userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: 'Are you sure you would like to record all of the outstanding tasks as completed without entering any data?', buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
      .then(response => {
        if (response) {
          reportActionService.completeActions(id!, reportPeriodContext.lastSelectedReportId!)
            .then(() => {
              userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
              getGroup();
            })
            .catch(err => {
              console.log(err)
              const message = err?.response?.data?.message || 'There was an error, please try again'
              userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' });
            })
        }
      })
  }

  return (
    <>
      {id && group &&
        <PageOuterContainer>
          <EmissionsOverviewGroupHeading group={group} getGroup={getGroup} />
          <EntityFindingsPanel entityType='group' entityId={group.id} />
          <Box sx={{ borderBottom: 1, borderColor: 'lightGrey.main' }}>
            <Tabs value={value} onChange={changeHandler}  >
              <Tab label='Emissions' {...a11yProps(emissionsTabIndex)} sx={{ textTransform: 'capitalize', borderRadius: 1 }} onClick={showGroupHandler} />
              {showTasksTab && <Tab label={
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                  <Typography>Tasks</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'primary.main', color: 'common.white', width: '25px', height: '25px', borderRadius: '100%' }}>
                    {group.reportActions.actionsOutstanding}
                  </Box>
                </Box>} {...a11yProps(tasksTabIndex)} sx={{ textTransform: 'capitalize', borderRadius: 1 }} onClick={showActionHandler} />}
              {showAssumptionsTab && <Tab label='Assumptions' {...a11yProps(assumptionsTabIndex)} sx={{ textTransform: 'capitalize', borderRadius: 1 }} onClick={showGroupHandler} />}
              <Tab label='Notes' {...a11yProps(notesTabIndex)} sx={{ textTransform: 'capitalize', borderRadius: 1 }} onClick={showGroupHandler} />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={tasksTabIndex}>
            {viewActions === 'actions' &&
              <EmissionsGroupButtons
                status={status}
                isTasksTab={true}
                doesGroupHaveActions={showTasksTab}
                outstandingActions={group.reportActions.actionsOutstanding}
                setStatus={setStatus}
                showToggle={true}
                group={group}
                completeAllActionsHandler={completeAllActionsHandler}
                showCategoriesHandler={showCategoriesHandler} />
            }
            <EmissionsActionsTab
              initialAction={initialAction}
              viewActions={viewActions}
              totalRecords={actionsTotalRecords}
              inputPage={inputPage}
              setInputPage={setInputPage}
              perPage={perPage}
              setPerPage={setPerPage}
              setOrderBy={setOrderBy}
              setOrder={setOrder}
              group={group}
              status={status}
              setViewActions={setViewActions}
              getGroup={getGroup}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={emissionsTabIndex}>
            <>
              {viewEmissions === 'groupTable' &&
                <>
                  <Emissions
                    queryString={`&groupId=${id}`}
                    backLink={`/emissions/overview/group/${id}?tabId=${emissionsTabIndex}`}
                    backText='Back to group'
                    includeDelete={true}
                    includeEdit={true}
                    initialReportingFilter={initialReportingFilter}
                    toolbarActions={!isReportPeriodLocked ? (
                      <EmissionsGroupButtons
                        status={status}
                        completeAllActionsHandler={completeAllActionsHandler}
                        showCategoriesHandler={showCategoriesHandler}
                        doesGroupHaveActions={showTasksTab}
                        setStatus={setStatus}
                        group={group}
                        outstandingActions={group.reportActions.actionsOutstanding}
                        actionsOnly={true}
                      />
                    ) : undefined}
                  />
                </>}
              {viewEmissions === 'categories' && (
                <>
                  {id && (isGoodsGroupId(id) || isSoldProductGroupId(id)) && group && selectedSubsidiaryId && conversionSources &&
                    <EmissionsGoodCatalogue
                      group={group}
                      activeCategoryId={activeCategoryId}
                      conversionSources={conversionSources}
                      backgroundColor={backgroundColor}
                      onSave={onSave}
                      isFormSubmitting={isFormSubmitting}
                      subsidiaries={subsidiaries}
                      handleTransactionSubmit={handleTransactionSubmit}
                      emissionFactorSourceHandler={emissionFactorSourceHandler}
                      selectedEmissionFactorSourceId={selectedEmissionFactorSourceId}
                      showCategoryFormHandler={showCategoryFormHandler}
                      setViewEmissions={setViewEmissions}
                      setActiveCategoryId={setActiveCategoryId}
                      setBackgroundColor={setBackgroundColor}
                      selectedSubsidiaryId={selectedSubsidiaryId}
                      setSelectedSubsidiaryId={setSelectedSubsidiaryId}
                    />}
                  {id && !isGoodsGroupId(id) && !isSoldProductGroupId(id) && group && conversionSources && subsidiaries && selectedSubsidiaryId &&
                    <Container
                      maxWidth='md'
                      sx={{ mb: 4 }}>
                      <EmissionsAddManual
                        group={group}
                        activeCategoryId={activeCategoryId}
                        conversionSources={conversionSources}
                        backgroundColor={backgroundColor}
                        onSave={onSave}
                        subsidiaries={subsidiaries}
                        isFormSubmitting={isFormSubmitting}
                        handleTransactionSubmit={handleTransactionSubmit}
                        selectedEmissionFactorSourceId={selectedEmissionFactorSourceId}
                        emissionFactorSourceHandler={emissionFactorSourceHandler}
                        showCategoryFormHandler={showCategoryFormHandler}
                        selectedSubsidiaryId={selectedSubsidiaryId}
                        setSelectedSubsidiaryId={setSelectedSubsidiaryId}
                      />
                    </Container>}
                </>)}
            </>
          </CustomTabPanel>
          {showAssumptionsTab &&
            <CustomTabPanel value={value} index={assumptionsTabIndex}>
              <EmissionsAssumptionsTable isGroupPage={true} groupId={id} />
            </CustomTabPanel>}
          <CustomTabPanel value={value} index={notesTabIndex}>
            {reportPeriodContext.lastSelectedReportId && (
              <NotesPanel
                relatedModelName='reportPeriod'
                relatedObjectId={reportPeriodContext.lastSelectedReportId}
                reportPeriodId={reportPeriodContext.lastSelectedReportId}
                groupId={id}
                canWrite={canWriteNotes}
                onAddAssumption={showAssumptionsTab ? () => setValue(assumptionsTabIndex) : undefined}
              />
            )}
          </CustomTabPanel>
        </PageOuterContainer >
      }
    </>
  )
}